import React from "react";
import moment from "moment/moment";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/layout/V2/Layout";
import ReviewInsightsCard from "../../components/ReviewInsightsCard";
import { useAuth } from "../../utils/Contexts/V2/AuthenticationContext";
import { getMpesaSpend } from "../../utils/requests";
import SpendChart from "../../components/SpendChart";
import { useBusinessContext } from "../../utils/Contexts/V2/BusinessContext";
import Spinner from "../../components/Spinner";
import useGoogleAnalytics from "../../utils/hooks/useGoogleAnalytics";

export default function InsightsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { business } = useBusinessContext();
  const { stats, loadingStats } = useGoogleAnalytics();

  const fromDate = moment().subtract(90, "days").format("YYYY-MM-DD");
  const toDate = moment().format("YYYY-MM-DD");

  const queryParams = new URLSearchParams({
    from_date: fromDate,
    to_date: toDate,
    spend: true,
  });

  const { data: transactions, isFetching } = useQuery(
    ["/mpesa-spend", queryParams.toString(), user?.store_number],
    async () => {
      try {
        const response = await getMpesaSpend(user.store_number, {
          queryParams,
        }).then((res) => {
          const list = res.data.weekly_transactions.map((transaction) => ({
            date: moment(transaction.date).format("MMM D"),
            "Weekly Transactions": transaction.total_amount,
          }));

          return {
            totalAmount: new Intl.NumberFormat("en-UK", {
              style: "currency",
              currency: "KES",
            }).format(res.data.total_amount),
            weeklyTransactions: list,
            rawTotalAmount: res.data.total_amount,
          };
        });
        return response;
      } catch (error) {
        console.log(error);
        return Promise.reject(error);
      }
    },
    {
      enabled: !!user?.store_number,
    }
  );

  return (
    <Layout header headerTitle="Insights">
      {isFetching ? (
        <div className="h-screen w-screen flex justify-center items-center">
          <Spinner />
        </div>
      ) : (
        <div className="max-w-screen-xl container mx-auto pt-20 pb-8 px-4">
          <div className="space-y-4">
            <div className="flex justify-between flex-row space-x-[1.5rem] lg:space-x-[3rem]">
              <div className="border p-4 w-[40vw] rounded-lg flex flex-col space-y-1">
                <svg
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="14.5" cy="14.5" r="14.5" fill="#8674FE" />
                  <path
                    d="M9.99984 11.6666C9.99984 11.2246 10.1754 10.8006 10.488 10.4881C10.8006 10.1755 11.2245 9.99992 11.6665 9.99992C12.1085 9.99992 12.5325 10.1755 12.845 10.4881C13.1576 10.8006 13.3332 11.2246 13.3332 11.6666C13.3332 12.1086 13.1576 12.5325 12.845 12.8451C12.5325 13.1577 12.1085 13.3333 11.6665 13.3333C11.2245 13.3333 10.8006 13.1577 10.488 12.8451C10.1754 12.5325 9.99984 12.1086 9.99984 11.6666ZM11.6665 8.33325C10.7824 8.33325 9.9346 8.68444 9.30948 9.30956C8.68436 9.93468 8.33317 10.7825 8.33317 11.6666C8.33317 12.5506 8.68436 13.3985 9.30948 14.0236C9.9346 14.6487 10.7824 14.9999 11.6665 14.9999C12.5506 14.9999 13.3984 14.6487 14.0235 14.0236C14.6486 13.3985 14.9998 12.5506 14.9998 11.6666C14.9998 10.7825 14.6486 9.93468 14.0235 9.30956C13.3984 8.68444 12.5506 8.33325 11.6665 8.33325ZM18.3332 12.4999C18.3332 12.2789 18.421 12.0669 18.5772 11.9107C18.7335 11.7544 18.9455 11.6666 19.1665 11.6666C19.3875 11.6666 19.5995 11.7544 19.7558 11.9107C19.912 12.0669 19.9998 12.2789 19.9998 12.4999C19.9998 12.7209 19.912 12.9329 19.7558 13.0892C19.5995 13.2455 19.3875 13.3333 19.1665 13.3333C18.9455 13.3333 18.7335 13.2455 18.5772 13.0892C18.421 12.9329 18.3332 12.7209 18.3332 12.4999ZM19.1665 9.99992C18.5035 9.99992 17.8676 10.2633 17.3987 10.7322C16.9299 11.201 16.6665 11.8369 16.6665 12.4999C16.6665 13.163 16.9299 13.7988 17.3987 14.2677C17.8676 14.7365 18.5035 14.9999 19.1665 14.9999C19.8295 14.9999 20.4654 14.7365 20.9343 14.2677C21.4031 13.7988 21.6665 13.163 21.6665 12.4999C21.6665 11.8369 21.4031 11.201 20.9343 10.7322C20.4654 10.2633 19.8295 9.99992 19.1665 9.99992ZM6.6665 18.7499C6.6665 17.5999 7.59984 16.6666 8.74984 16.6666H14.5832C15.7332 16.6666 16.6665 17.5999 16.6665 18.7499V18.8299C16.6652 18.8907 16.6608 18.9513 16.6532 19.0116C16.6385 19.1576 16.6135 19.3024 16.5782 19.4449C16.4664 19.9061 16.2654 20.341 15.9865 20.7249C15.2798 21.6966 13.9632 22.4999 11.6665 22.4999C9.36984 22.4999 8.05317 21.6966 7.3465 20.7249C6.98108 20.2204 6.7515 19.6304 6.67984 19.0116C6.67314 18.9507 6.66869 18.8895 6.6665 18.8283V18.7499ZM8.33317 18.7933C8.34008 18.8809 8.35401 18.9678 8.37484 19.0533C8.41984 19.2416 8.5115 19.4933 8.69484 19.7449C9.02817 20.2049 9.79484 20.8333 11.6665 20.8333C13.5365 20.8333 14.3032 20.2049 14.6382 19.7449C14.8399 19.4654 14.9646 19.1378 14.9998 18.7949V18.7499C14.9998 18.6394 14.9559 18.5334 14.8778 18.4553C14.7997 18.3772 14.6937 18.3333 14.5832 18.3333H8.74984C8.63933 18.3333 8.53335 18.3772 8.45521 18.4553C8.37707 18.5334 8.33317 18.6394 8.33317 18.7499V18.7933ZM19.1648 21.6666C18.6418 21.6718 18.1199 21.6158 17.6098 21.4999C17.9146 21.0189 18.1307 20.4871 18.2482 19.9299C18.5065 19.9733 18.8098 19.9999 19.1648 19.9999C20.6482 19.9999 21.2115 19.5466 21.4315 19.2716C21.5538 19.1193 21.6342 18.9378 21.6648 18.7449V18.7366C21.6614 18.6284 21.6159 18.5258 21.5382 18.4505C21.4604 18.3753 21.3564 18.3332 21.2482 18.3333H18.3098C18.2441 17.7368 18.0354 17.1651 17.7015 16.6666H21.2498C22.3998 16.6666 23.3332 17.5999 23.3332 18.7499V18.7783C23.3317 18.8301 23.3278 18.8818 23.3215 18.9333C23.2553 19.4376 23.0525 19.9143 22.7348 20.3116C22.1198 21.0783 21.0182 21.6666 19.1665 21.6666H19.1648Z"
                    fill="white"
                  />
                </svg>
                <p className="text-sm">
                  {loadingStats ? "Loading..." : stats.reach}
                </p>
                <div className="flex justify-between space-x-2">
                  <h3 className="text-xs sm:text-sm">People Reached</h3>
                  {/* <p className="text-xs sm:text-sm text-[#45C78A]">+10%</p> */}
                </div>
              </div>
              <div className="border p-4 w-[40vw] rounded-lg flex flex-col space-y-1">
                <svg
                  width="29"
                  height="29"
                  viewBox="0 0 29 29"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="14.5" cy="14.5" r="14.5" fill="#FFAD03" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.3752 11.3125V13.9617C11.5181 13.3879 10.9585 12.4175 10.9585 11.3125C10.9585 9.54875 12.3822 8.125 14.146 8.125C15.9097 8.125 17.3335 9.54875 17.3335 11.3125C17.3335 12.4175 16.7739 13.3879 15.9168 13.9617V11.3125C15.9168 10.335 15.1235 9.54167 14.146 9.54167C13.1685 9.54167 12.3752 10.335 12.3752 11.3125ZM16.1293 15.6404L19.3452 17.2413C19.7206 17.3971 19.9897 17.7796 19.9897 18.2188C19.9897 18.2612 19.9827 18.3108 19.9756 18.3604L19.9756 18.3604L19.4443 22.0933C19.3664 22.6104 18.9556 23 18.4243 23H13.6147C13.3243 23 13.0552 22.8796 12.8639 22.6883L9.36475 19.1892L9.92433 18.6225C10.066 18.4808 10.2643 18.3888 10.4839 18.3888C10.5264 18.3888 10.5626 18.395 10.5988 18.4013C10.6169 18.4045 10.635 18.4076 10.6539 18.41L13.0835 18.92V11.3125C13.0835 10.7246 13.5581 10.25 14.146 10.25C14.7339 10.25 15.2085 10.7246 15.2085 11.3125V15.5625H15.7468C15.8814 15.5625 16.0089 15.5908 16.1293 15.6404Z"
                    fill="white"
                  />
                </svg>
                <p className="text-sm">
                  {loadingStats ? "Loading..." : stats.engagement}
                </p>
                <div className="flex justify-between space-x-2">
                  <h3 className="text-xs sm:text-sm">Engagement</h3>
                  {/* <p className="text-xs sm:text-sm text-[#EE421E]">-2%</p> */}
                </div>
              </div>
            </div>

            <div>
              <button
                type="button"
                style={{ boxShadow: "0px 1px 3px 1px #00000014" }}
                className="flex border rounded-xl px-2 py-2 items-center space-x-2"
                onClick={() => navigate("/add-photos")}
              >
                <div className="h-12 w-12 p-3 flex items-center justify-center rounded-full bg-primary-10">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 7.5C15.0001 7.10204 15.1583 6.72044 15.4398 6.43913C15.7213 6.15783 16.103 5.99987 16.501 6C16.899 6.00013 17.2806 6.15835 17.5619 6.43984C17.8432 6.72133 18.0011 7.10304 18.001 7.501C18.0009 7.89896 17.8427 8.28056 17.5612 8.56187C17.2797 8.84317 16.898 9.00113 16.5 9.001C16.102 9.00087 15.7204 8.84265 15.4391 8.56116C15.1578 8.27967 14.9999 7.89796 15 7.5ZM4 20H18C18 20.5304 17.7893 21.0391 17.4142 21.4142C17.0391 21.7893 16.5304 22 16 22H4C2.9 22 2 21.1 2 20V8C2 7.46957 2.21071 6.96086 2.58579 6.58579C2.96086 6.21071 3.46957 6 4 6V20ZM22 4V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H8C7.46957 18 6.96086 17.7893 6.58579 17.4142C6.21071 17.0391 6 16.5304 6 16V4C6 3.46957 6.21071 2.96086 6.58579 2.58579C6.96086 2.21071 7.46957 2 8 2H20C20.5304 2 21.0391 2.21071 21.4142 2.58579C21.7893 2.96086 22 3.46957 22 4ZM8 4V10.333L11 7L15.855 12.395L16.511 11.664C16.6986 11.4552 16.928 11.2881 17.1844 11.1738C17.4407 11.0595 17.7183 11.0004 17.999 11.0004C18.2797 11.0004 18.5573 11.0595 18.8136 11.1738C19.07 11.2881 19.2994 11.4552 19.487 11.664L20 12.234V4H8Z"
                      // fill="#4B1D8F"
                      className="fill-primary-50"
                    />
                  </svg>
                </div>
                <div className="flex-col text-xs text-start">
                  <h3 className="text-[#1A1A1A] font-semibold text-sm">
                    Post more photos
                  </h3>
                  <p>
                    Businesses with more photos typically receive more clicks to
                    their webpages.
                  </p>
                </div>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_1316_4045)">
                    <path
                      d="M13.1722 12L8.22217 7.04999L9.63617 5.63599L16.0002 12L9.63617 18.364L8.22217 16.95L13.1722 12Z"
                      className="fill-primary-50"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_1316_4045">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div>
              <h3 className="text-[#1A1A1A] mb-2">
                How Customers Rate Your Business
              </h3>
              {business ? (
                <ReviewInsightsCard data={business?.reviews} />
              ) : (
                <div className="flex items-center justify-center w-full">
                  <Spinner />
                </div>
              )}
            </div>
            {user?.store_number && user?.country?.country_code === "KE" ? (
              <div>
                <h3 className="font-semibold">Spending Overview</h3>
                {transactions?.rawTotalAmount > 0 ? (
                  <div>
                    <p className="font-semibold">{transactions?.totalAmount}</p>
                    <div className="container h-full">
                      <SpendChart data={transactions?.weeklyTransactions} />
                    </div>
                  </div>
                ) : (
                  <p>
                    You have not made any transactions. Link your MPESA till in
                    the profile page to get spend insights.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Layout>
  );
}
